import { FC, useEffect, useState } from 'react';
import { useInterval } from '@trukt/react-hooks/useInterval';
import { Icon } from '../../../../../components/Icon';
import ProgressBar from '../../../../../components/ProgressBar';
import { Typography } from '../../../../../components/Typography';

import { ProgressNotifierItemProps } from './ProgressNotifierItem.types';

export const ProgressNotifierItem: FC<ProgressNotifierItemProps> = ({
  data,
  onRemove = () => {},
}) => {
  const [count, setCount] = useState(0);

  useInterval(() => {
    if (count < 90 || ((data.isSuccess || data.isError || data.isWarning) && count < 100)) {
      setCount(count + 1);
    }
  }, 20);

  useEffect(() => {
    if ((data.isSuccess || data.isError) && count < 100) {
      setCount(100);
    }
  }, [data, count]);

  useEffect(() => {
    let timeoutId: any;

    if (count === 100 && data.isSuccess) {
      timeoutId = setTimeout(() => onRemove(data.id), 1000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [count]);

  const handleGetCurrentColorStatus = () => {
    if (data.isError) return '!text-error-400';
    if (data.isWarning) return '!text-warning-400';
    return data.isSuccess ? '!text-success-400' : '!text-primary-400';
  };

  const handleGetCurrentBgColor = () => {
    if (data.isError) return '!bg-error-400';
    if (data.isWarning) return '!bg-warning-400';
    return data.isSuccess ? '!bg-success-400' : '!bg-primary-400';
  };

  const handleGetCurrentFillStatus = () => {
    if (data.isError) return '!fill-error-400';
    if (data.isWarning) return '!fill-warning-400';
    return data.isSuccess ? '!fill-success-400' : '!fill-primary-400';
  };

  const colorStatus = handleGetCurrentColorStatus();

  return (
    <div
      role="progressbar"
      data-testid="progress__notifier__item"
      className={[
        'pointer-events-auto bg-bg-secondary shadow-primary-outset',
        'rounded-lg p-2',
      ].join(' ')}
    >
      <div className="mb-2 flex items-center text-left">
        <Typography variant="h6" className="flex-[1]" disableEllipse>
          {data.label}
        </Typography>

        <span data-testid="remove__button__wrapper">
          <Icon
            onClick={() => onRemove(data.id)}
            name="x"
            title="Close Notification"
            className={colorStatus}
            fill="success"
          />
        </span>
      </div>

      {count === 100 ? (
        <>
          <div className="flex items-center justify-end gap-2 text-right">
            <Typography className={[colorStatus, 'whitespace-pre-wrap'].join(' ')}>
              {(() => {
                if (data.isSuccess) return 'Complete';
                if (data.isWarning) return data.warningMessage;
                return data.errorMessage || 'Could Not Complete';
              })()}
            </Typography>

            {data.isSuccess ? (
              <Icon
                variant="display"
                name="check-circle"
                title="Sucessful Notification"
                color="white"
                fill="success"
              />
            ) : (
              <Icon
                title="Warning Notification"
                variant="display"
                name="alert-circle"
                color="white"
                className={handleGetCurrentFillStatus()}
              />
            )}
          </div>

          <div>{data.children}</div>
        </>
      ) : (
        <ProgressBar width={count} color={handleGetCurrentBgColor()} />
      )}
    </div>
  );
};
